import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  ApiError,
  FsEntry,
  deleteDeviceFile,
  deviceDownloadUrl,
  listDeviceFs,
  uploadDeviceFile,
} from './api';

/**
 * The remote half of an AnyDesk-style file manager. There is no local pane:
 * the browser already is one — downloads land in the user's Downloads folder
 * with the browser's own progress and cancel, and uploads come from its file
 * picker. Building a second, worse file list out of the File System Access API
 * would add a Chromium-only dependency and buy nothing.
 */

export interface FileBrowserDialogProps {
  deviceId: string;
  deviceName: string;
  open: boolean;
  onClose: () => void;
}

/** Windows paths use \, POSIX uses /. The box's own roots tell us which. */
function separatorFor(path: string): string {
  return path.includes('\\') || /^[A-Za-z]:$/.test(path) ? '\\' : '/';
}

function joinPath(dir: string, name: string): string {
  if (dir === '') return name;
  const sep = separatorFor(dir);
  return dir.endsWith(sep) ? `${dir}${name}` : `${dir}${sep}${name}`;
}

/** '' when already at the top; otherwise the containing directory. */
export function parentOf(path: string): string {
  if (path === '') return '';
  const sep = separatorFor(path);
  const trimmed = path.endsWith(sep) && path.length > 1 ? path.slice(0, -1) : path;
  const cut = trimmed.lastIndexOf(sep);
  if (cut < 0) return '';
  // 'C:\x' → 'C:\' (a drive root keeps its separator); '/srv/x' → '/srv'.
  const parent = trimmed.slice(0, cut);
  if (parent === '' ) return sep === '/' ? '/' : '';
  if (/^[A-Za-z]:$/.test(parent)) return `${parent}\\`;
  return parent;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

function formatWhen(mtimeMs: number): string {
  if (!mtimeMs) return '—';
  return new Date(mtimeMs).toLocaleString();
}

export function FileBrowserDialog({ deviceId, deviceName, open, onClose }: FileBrowserDialogProps) {
  const [path, setPath] = useState('');
  const [entries, setEntries] = useState<FsEntry[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyPath, setBusyPath] = useState<string | null>(null);
  const [upload, setUpload] = useState<{ name: string; sent: number; total: number } | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const load = useCallback(
    async (target: string) => {
      setLoading(true);
      setError(null);
      try {
        const result = await listDeviceFs(deviceId, target);
        // Folders first, then files, each A→Z — the order every file manager
        // uses, and the one that makes a deep tree navigable by eye.
        setEntries(
          [...result.entries].sort((a, b) =>
            a.isDir === b.isDir ? a.name.localeCompare(b.name) : a.isDir ? -1 : 1,
          ),
        );
        setTruncated(result.truncated);
        setPath(target);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [deviceId],
  );

  useEffect(() => {
    if (open) void load('');
  }, [open, load]);

  const crumbs = useMemo(() => {
    if (path === '') return [];
    const sep = separatorFor(path);
    const parts = path.split(sep).filter((p) => p.length > 0);
    let acc = '';
    return parts.map((part, i) => {
      acc = i === 0 ? (sep === '\\' ? `${part}\\` : `/${part}`) : joinPath(acc, part);
      return { label: part, path: acc };
    });
  }, [path]);

  const download = async (entry: FsEntry) => {
    setBusyPath(entry.name);
    setError(null);
    try {
      const url = await deviceDownloadUrl(deviceId, joinPath(path, entry.name));
      // Let the browser own the transfer: a real download entry, real
      // progress, resumable by the user, never buffered through JS memory.
      const link = document.createElement('a');
      link.href = url;
      link.download = entry.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyPath(null);
    }
  };

  const remove = async (entry: FsEntry) => {
    if (
      !window.confirm(
        `Delete ${entry.name} from ${deviceName}? This removes the file on the box and cannot be undone.`,
      )
    ) {
      return;
    }
    setBusyPath(entry.name);
    setError(null);
    try {
      await deleteDeviceFile(deviceId, joinPath(path, entry.name));
      await load(path);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyPath(null);
    }
  };

  const uploadHere = async (file: File) => {
    setError(null);
    setUpload({ name: file.name, sent: 0, total: file.size });
    try {
      const handle = uploadDeviceFile(
        deviceId,
        file,
        (sent, total) => setUpload({ name: file.name, sent, total }),
        path,
      );
      await handle.promise;
      await load(path);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err),
      );
    } finally {
      setUpload(null);
    }
  };

  const atRoot = path === '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          Files on <strong>{deviceName}</strong>
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={() => void load(path)} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Tooltip title={atRoot ? 'Already at the top' : 'Up one folder'}>
            <span>
              <IconButton
                size="small"
                disabled={atRoot || loading}
                aria-label="Up one folder"
                onClick={() => void load(parentOf(path))}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Breadcrumbs sx={{ flexGrow: 1, fontSize: 14 }}>
            <Link
              component="button"
              underline="hover"
              color={atRoot ? 'text.primary' : 'inherit'}
              onClick={() => void load('')}
            >
              This device
            </Link>
            {crumbs.map((crumb) => (
              <Link
                key={crumb.path}
                component="button"
                underline="hover"
                color={crumb.path === path ? 'text.primary' : 'inherit'}
                onClick={() => void load(crumb.path)}
              >
                {crumb.label}
              </Link>
            ))}
          </Breadcrumbs>
          <Tooltip
            title={
              atRoot
                ? 'Open a folder first — a file has to land somewhere'
                : `Upload into ${path}`
            }
          >
            <span>
              <Button
                size="small"
                startIcon={<UploadFileIcon fontSize="small" />}
                disabled={atRoot || upload !== null}
                onClick={() => fileInput.current?.click()}
                sx={{ textTransform: 'none' }}
              >
                Upload here
              </Button>
            </span>
          </Tooltip>
          <input
            ref={fileInput}
            type="file"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void uploadHere(file);
            }}
          />
        </Box>

        {loading && <LinearProgress />}
        {upload && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption">
              Uploading {upload.name} — {formatBytes(upload.sent)} of {formatBytes(upload.total)}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={upload.total ? (upload.sent / upload.total) * 100 : 0}
            />
          </Box>
        )}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {truncated && (
          <Alert severity="info" sx={{ m: 2 }}>
            This folder holds more than 2000 items — only the first 2000 are shown.
          </Alert>
        )}

        <TableContainer sx={{ maxHeight: 420 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="right">Size</TableCell>
                <TableCell>Modified</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      This folder is empty.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {entries.map((entry) => (
                <TableRow key={entry.name} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {entry.isDir ? (
                        <FolderIcon fontSize="small" color="primary" />
                      ) : (
                        <InsertDriveFileOutlinedIcon fontSize="small" color="disabled" />
                      )}
                      {entry.isDir ? (
                        <Link
                          component="button"
                          underline="hover"
                          onClick={() => void load(joinPath(path, entry.name))}
                          sx={{ textAlign: 'left' }}
                        >
                          {entry.name}
                        </Link>
                      ) : (
                        <span>{entry.name}</span>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{entry.isDir ? '—' : formatBytes(entry.size)}</TableCell>
                  <TableCell>{formatWhen(entry.mtimeMs)}</TableCell>
                  <TableCell align="right">
                    {!entry.isDir && (
                      <>
                        <Tooltip title="Download to this computer">
                          <span>
                            <IconButton
                              size="small"
                              aria-label={`Download ${entry.name}`}
                              disabled={busyPath !== null}
                              onClick={() => void download(entry)}
                            >
                              {busyPath === entry.name ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DownloadIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete on the box">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              aria-label={`Delete ${entry.name}`}
                              disabled={busyPath !== null}
                              onClick={() => void remove(entry)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
}
